// Owns the Cropper instance and everything that reads pixels back off it. Extracted from
// image_uploads_controller.js: the cropper's only coupling to the controller is the image element
// it wraps plus five sizing options, so it takes those by value and needs no callback into the
// controller and no teardown handle of its own -- the controller calls destroy().
//
// Cropper attaches a window resize listener that only destroy() removes, so every construct here
// must be matched by a destroy.
export default class ImageCropper {
  constructor(imageElement, options) {
    this.imageElement = imageElement;
    this.options = options;
    this.cropper = null;
  }

  init() {
    const { width, height, aspectRatio } = this.options;

    if (height && width) {
      this.cropper = new Cropper(this.imageElement, {
        aspectRatio: aspectRatio,
        viewMode: 0,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
        guides: false,
        autoCropArea: this.autoCropArea()
      });
    } else {
      this.cropper = new Cropper(this.imageElement, {
        viewMode: 0,
        guides: false
      });
    }
  }

  destroy() {
    if (!this.cropper) { return; }

    this.cropper.destroy();
    this.cropper = null;
  }

  rotate() {
    this.cropper.rotate(90);

    const canvasData = this.cropper.getCanvasData();

    this.cropper.setAspectRatio(canvasData.width / canvasData.height);
  }

  autoCropArea() {
    const naturalWidth = this.imageElement.naturalWidth;
    const naturalHeight = this.imageElement.naturalHeight;

    if (naturalWidth == undefined || naturalHeight == undefined || naturalWidth <= 450 || naturalHeight <= 450) {
      return 1;
    }

    return 450 / Math.min(naturalWidth, naturalHeight);
  }

  // Returns undefined when nothing has been cropped yet, matching the original inline version.
  canvas() {
    if (!this.cropper) { return undefined; }

    const dpr = window.devicePixelRatio || 1;
    const cropData = this.cropper.getData(true);
    const width = cropData.width;
    const height = cropData.height;

    const canvas = this.cropper.getCroppedCanvas({
      width: width * dpr,
      height: height * dpr,
      fillColor: this.options.fillColor,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high'
    });

    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    if (this.options.circular) {
      return this.circularCanvas(canvas);
    }

    return canvas;
  }

  circularCanvas(sourceCanvas) {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const width = sourceCanvas.width;
    const height = sourceCanvas.height;

    canvas.width = width;
    canvas.height = height;

    context.imageSmoothingEnabled = true;
    context.drawImage(sourceCanvas, 0, 0, width, height);
    context.globalCompositeOperation = 'destination-in';

    context.beginPath();
    context.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, 2 * Math.PI, true);
    context.fill();

    return canvas;
  }
}
